import {
  ErpOrderState,
  getOrderPaymentOverview,
  isActiveOrderPaymentStatus,
} from '../types/api/erp'
import { NumberUtils } from './NumberUtils'
import { DEFAULT_DECIMALS } from '../currency'

export type GroupPayment = {
  id?: number | null
  amount: number
  status: string
  type?: string
}

export type GroupPaymentMember = {
  id: number
  ref: string
  status: string
  items: readonly { price: number }[]
  payments: readonly GroupPayment[]
}

export type GroupPaymentOrder = {
  id: number
  ref: string
  amount: number
}

export type GroupPaymentTotals = {
  orders: GroupPaymentOrder[]
  total: number
  paid: number
  remaining: number
}

const hasActive = (payments: readonly GroupPayment[]) =>
  payments.some((payment) => isActiveOrderPaymentStatus(payment.status))

const hasPosted = (payments: readonly GroupPayment[]) =>
  payments.some((payment) => payment.status === 'posted')

const isUnsupportedGroupPayment = (payment: GroupPayment) => {
  if (payment.amount < 0) {
    return true
  }
  if (payment.status !== 'posted') {
    return false
  }
  return payment.type !== undefined && payment.type !== 'inbound'
}

const isSamePaymentDetails = (previous: GroupPayment, payment: GroupPayment) =>
  previous.amount === payment.amount &&
  previous.status === payment.status &&
  previous.type === payment.type

const getUnique = (members: readonly GroupPaymentMember[]) => {
  const payments = new Map<number, GroupPayment>()
  const hasInvalidPayments = members
    .flatMap((member) => member.payments)
    .some((payment) => {
      if (payment.id == null) {
        return payment.status === 'posted'
      }
      const previous = payments.get(payment.id)
      if (previous && !isSamePaymentDetails(previous, payment)) {
        return true
      }
      payments.set(payment.id, payment)
      return isUnsupportedGroupPayment(payment)
    })
  if (hasInvalidPayments) {
    return null
  }
  return [...payments.values()]
}

const hasPaidCanceledOrder = (members: readonly GroupPaymentMember[]) =>
  members.some(
    (member) =>
      member.status === ErpOrderState.cancel && hasPosted(member.payments)
  )

// Amounts are summed as integers in the currency's minor units
const getScale = (decimals: number) => Math.pow(10, decimals)

const getOrderTotals = (
  members: readonly GroupPaymentMember[],
  decimals: number
): GroupPaymentOrder[] =>
  members
    .filter((member) => member.status !== ErpOrderState.cancel)
    .map((member) => ({
      id: member.id,
      ref: member.ref,
      amount: Math.max(
        NumberUtils.round.decimal(
          getOrderPaymentOverview(member.items, []).total,
          decimals
        ),
        0
      ),
    }))
    .filter((order) => order.amount > 0)
    .sort((left, right) => left.id - right.id)

const getTotalIntegerAmount = (
  orders: readonly GroupPaymentOrder[],
  scale: number
) => Math.round(orders.reduce((sum, order) => sum + order.amount, 0) * scale)

const getPaidIntegerAmount = (
  payments: readonly GroupPayment[],
  scale: number
) =>
  payments.reduce(
    (sum, payment) =>
      sum +
      (payment.status === 'posted' ? Math.round(payment.amount * scale) : 0),
    0
  )

const allocateRemaining = (
  orders: readonly GroupPaymentOrder[],
  remaining: number,
  decimals = DEFAULT_DECIMALS
): GroupPaymentOrder[] => {
  const scale = getScale(decimals)
  const totalIntegerAmount = getTotalIntegerAmount(orders, scale)
  const remainingIntegerAmount = Math.round(remaining * scale)
  if (remainingIntegerAmount === 0) {
    return []
  }

  // These are local weights, not ERP per-order balances. The regular endpoint receives
  // all group IDs and one amount. Cumulative rounding keeps the local total exact.
  let cumulativeIntegerAmount = 0
  let allocatedIntegerAmount = 0
  return orders.map((order) => {
    cumulativeIntegerAmount += Math.round(order.amount * scale)
    const nextAllocatedIntegerAmount = Math.round(
      (remainingIntegerAmount * cumulativeIntegerAmount) / totalIntegerAmount
    )
    const amount = (nextAllocatedIntegerAmount - allocatedIntegerAmount) / scale
    allocatedIntegerAmount = nextAllocatedIntegerAmount
    return { ...order, amount }
  })
}

// Callers must supply the complete, validated group, not a partial history page.
const getTotals = (
  members: readonly GroupPaymentMember[],
  decimals = DEFAULT_DECIMALS
): GroupPaymentTotals | null => {
  // A canceled order's payment may be refunded or transferred; IDs alone do not tell us which.
  if (hasPaidCanceledOrder(members)) {
    return null
  }
  const payments = getUnique(members)
  if (!payments) {
    return null
  }

  const scale = getScale(decimals)
  const orders = getOrderTotals(members, decimals)
  const totalIntegerAmount = getTotalIntegerAmount(orders, scale)
  const paidIntegerAmount = getPaidIntegerAmount(payments, scale)
  const remainingIntegerAmount = Math.max(
    totalIntegerAmount - paidIntegerAmount,
    0
  )

  return {
    orders,
    total: totalIntegerAmount / scale,
    paid: paidIntegerAmount / scale,
    remaining: remainingIntegerAmount / scale,
  }
}

export const GroupPayments = {
  hasPaidCanceledOrder,
  getUnique,
  getTotals,
  allocateRemaining,
  hasActive,
  hasPosted,
}
